import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@components/common/popover'
import { Button } from '@components/common/button'
import { DictType, Locale } from '@pma/types'
import { Check, Globe2 } from 'lucide-react'
import { parseCookies, setCookie } from 'nookies'
import { locales } from '@pma/locale/dictionaries'
import useTranslator from '@hooks/use-translator'
import { ACCESS_TOKEN, LOCALE } from '@pma/utils/constants'
import { cn } from '@pma/utils/helpers'
import { Separator } from '@components/common/separator'
import { useTranslation } from 'react-i18next'
import { ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAuth } from '@pma/stores/auth-store'
import axios from 'axios'
import {
  ORGANIZATION_LANGUAGE_ENDPOINT,
  SERVER_URL,
  USER_LANGUAGE_ENDPOINT,
} from '@pma/utils/api-routes'
import { toast } from '@pma/hooks/use-toast'

interface Props {
  children?: ReactNode
  changeOrg?: CallableFunction
  orgLocale?: Locale
}
const LocaleSwitcher = ({ children, changeOrg, orgLocale }: Props) => {
  const { translate, locale: language } = useTranslator()
  const { i18n } = useTranslation()

  const { authConfig, signIn } = useAuth()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const orgId = authConfig?.last_organization_id

  const changeLocale = (locale: Locale) => {
    i18n.changeLanguage(locale)

    setCookie(null, LOCALE, locale, {
      maxAge: 1000 * 60 * 60 * 24 * 360,
      sameSite: 'strict',
      path: '/',
    })

    if (changeOrg) {
      changeWorkspaceLanguageMutation.mutate(locale)
      changeUserLanguageMutation.mutate(locale)
    }
  }

  const changeWorkspaceLanguage = async (locale: Locale) => {
    const response = await axios.post(
      SERVER_URL + ORGANIZATION_LANGUAGE_ENDPOINT,
      {
        organization_id: orgId,
        language: locale,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const changeWorkspaceLanguageMutation = useMutation({
    mutationKey: ['workspace-language', orgId],
    mutationFn: changeWorkspaceLanguage,

    onSuccess: (data) => {
      changeOrg && changeOrg()

      toast({
        title: translate('Success'),
        description: `${translate('Workspace language')} ${translate('set to')} ${translate(
          locales.find(
            (locale) => locale.shortName.toLocaleLowerCase() == language,
          )?.name || '',
        )}`,
        variant: 'success',
      })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate('Error'),
        description: translate(errorResponse || 'Unable to update default language'),
        variant: 'error',
      })
    },
  })

  const changeUserLanguage = async (locale: Locale) => {
    const response = await axios.post(
      SERVER_URL + USER_LANGUAGE_ENDPOINT,
      {
        selected_language: locale,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const changeUserLanguageMutation = useMutation({
    mutationKey: ['user-language'],
    mutationFn: changeUserLanguage,

    onSuccess: (data) => {
      signIn({ ...data })

      toast({
        title: translate('Success'),
        description: `${translate('Default language')} ${translate('updated')}`,
        variant: 'success',
      })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate('Error'),
        description: translate(errorResponse || 'Unable to update default language'),
        variant: 'error',
      })
    },
  })

  return (
    <Popover>
      <PopoverTrigger>
        {children ? (
          children
        ) : (
          <Button
            className='capitalize h-7 w-[60px] cursor-pointer bg-transparent text-white'
            variant={'outline'}
            size={'sm'}
          >
            <Globe2 className='ltr:mr-2 rtl:ml-2 mx-1' size={14} />
            {language}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className='w-fit mt-4 mx-1'>
        {locales.map(({ locale, name }: DictType, index: number) => (
          <PopoverClose
            key={locale + index}
            className={cn(
              'flex flex-col  w-[140px] overflow-hidden text-sm font-medium leading-none pb-2',
              changeOrg && orgLocale === locale && 'text-primary',
              !changeOrg && locale === language && 'text-primary',
            )}
            onClick={() => changeLocale(locale)}
          >
            <div className='flex  items-center w-full'>
              <span className='flex-1 text-left'>{name}</span>

              {changeOrg && orgLocale === locale && (
                <Check className='ltr:ml-4 rtl:mr-4' />
              )}
              {!changeOrg && locale === language && (
                <Check className='ltr:ml-4 rtl:mr-4' />
              )}
            </div>
            <Separator className='my-1 w-full' />
          </PopoverClose>
        ))}
      </PopoverContent>
    </Popover>
  )
}

export default LocaleSwitcher
